/**
 * C-16 — what `DELETE /resources/:id` actually means, and how to talk about
 * its failure.
 *
 * `resources.service.ts#remove` blocks the delete with a 409 exactly when the
 * resource has a live session (`SCHEDULED` | `ACTIVE` | `PAUSED`) — and that
 * 409 carries no machine-readable `code`, only a plain message
 * (`'Stop the running sessions on this resource first'`). Pricing's
 * `refusalSentence` (`Pages/Pricing/lib/refusals.ts`) picks its sentence by
 * `code`, so a `code`-less refusal falls straight to its generic one — "That
 * did not work" to the very person who needs to hear "stop the session
 * first." This file is the one place that decides which sentence a failed
 * delete gets, so both call sites (the board's own delete button and the one
 * inside the edit form) read the same reason off the same failure.
 */

/** Which of the two sentences a failed `DELETE /resources/:id` gets. */
export type DeleteResourceOutcome = 'blockedByLiveSession' | 'failed';

/**
 * Reads the one distinction the resource-delete endpoint's failures actually
 * carry: 409 always means "a session is still live on it" (the only
 * `ConflictException` `resources.service.ts#remove` throws), and everything
 * else — a stale row, a dropped connection, a 500 — is a plain failure with
 * nothing more specific to say.
 */
export function deleteResourceOutcome(status: number | undefined): DeleteResourceOutcome {
  return status === 409 ? 'blockedByLiveSession' : 'failed';
}
